import { defineStore } from "pinia";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { apiUser } from "@/components/toLoad/api";
import { capture, captureException, group, register } from "@/posthog";

function syncPostHogCompany(user) {
  const companyId = user?.company;
  if (!companyId) return;

  const role = user.job || (user.status != null ? String(user.status) : null);

  /* eslint-disable camelcase */
  group("company", companyId, {
    name: companyId,
  });
  register({
    company_id: companyId,
    role,
  });
  /* eslint-enable camelcase */
}

export const useUserStore = defineStore("user", {
  state: () => ({
    user: null,
    loading: false,
    themeWrite: null,
  }),
  actions: {
    resetState() {
      this.user = null;
      this.loading = false;
      this.themeWrite = null;
    },

    cleanupListeners() {},

    async ensureActiveCompany(uid, user) {
      if (user?.company) return user;

      try {
        const { data } = await apiUser({
          method: "get",
          url: `/users/${uid}/companies`,
        });
        const first = (data.documents || [])[0];
        if (!first?.id) return user;

        await apiUser({
          method: "patch",
          url: "/me",
          data: { company: first.id },
        });
        return { ...user, company: first.id };
      } catch (error) {
        captureException(error, { flow: "ensure_active_company" });
        return user;
      }
    },

    async initUserListener(uid) {
      if (!uid) {
        this.resetState();
        return;
      }

      this.loading = true;
      try {
        const { data } = await apiUser({ method: "get", url: "/me" });
        if (this.themeWrite && data.settings?.appTheme === this.themeWrite)
          this.themeWrite = null;
        this.user = await this.ensureActiveCompany(uid, data);
        syncPostHogCompany(this.user);
      } catch (error) {
        console.error("Error fetching user:", error);
        captureException(error, { flow: "user_listener" });
        this.user = null;
      } finally {
        this.loading = false;
      }
    },

    async updateTheme(theme) {
      const uid = firebase.auth().currentUser?.uid;
      if (!uid || (theme !== "dark" && theme !== "light")) return;

      this.themeWrite = theme;
      try {
        await apiUser({
          method: "patch",
          url: "/me",
          data: { settings: { appTheme: theme } },
        });
        await this.initUserListener(uid);
      } catch (error) {
        console.error("Error saving theme:", error);
        captureException(error, { flow: "user_theme_update" });
        if (this.themeWrite === theme)
          this.themeWrite = null;
      }
    },

    async updateUserCompany(companyId) {
      const uid = firebase.auth().currentUser?.uid;
      if (!uid) return;

      const previousCompanyId = this.user?.company || null;
      await apiUser({
        method: "patch",
        url: "/me",
        data: { company: companyId },
      });
      await this.initUserListener(uid);

      /* eslint-disable camelcase */
      syncPostHogCompany({
        ...this.user,
        company: companyId,
      });
      capture("company_switched", {
        previous_company_id: previousCompanyId,
        company_id: companyId,
      });
      /* eslint-enable camelcase */
    },

    stopUserListener() {
      this.resetState();
    },
  },
});
